import { UserRole } from "../../utils/api/endpoints/user/credentials";
import { CTBReport } from "../../utils/api/endpoints/reports/parseReports";
import {
  ReportState,
  ReportStatus
} from "../../utils/api/endpoints/reports/reports";
import useUserCredentials from "../../utils/hooks/user/useUserCredentials";
import Pill from "../pills/Pill";
import {
  FiAlertCircle,
  FiCheckCircle,
  FiClock,
  FiInfo,
  FiXCircle
} from "react-icons/fi";

export enum ReportPillStatus {
  DRAFT = "Draft",
  REVIEW = "In Review",
  APPROVED = "Approved",
  AWAITING_FIX = "Awaiting Fix",
  BUSINESS_REQUEST_INFO = "Request more info",
  BUSINESS_ACCEPT_RISK = "Business Accepts Risk",
  BUSINESS_NON_ACTIONABLE_ISSUE = "Non-Actionable Issue",
  OUT_OF_SCOPE = "out of scope",
  // QA_DISAGREES = "QA Disagrees",
  QA_MODIFIED = "QA Modified",
  REJECTED = "Rejected",
  PAYMENT_PENDING = "Payment Pending",
  CLOSED = "Closed",
  DELETED = "deleted",
  // New statuses
  RETEST_REQUESTED = "Retest Requested",
  RETEST_IN_PROCESS = "Retest In Process",
  FIX_VERIFIED = "Fix Verified",
  FIX_FAILED = "Fix Failed",
  QA_REVIEW_IN_PROCESS = "QA Review In Process",
  FIX_APPROVED = "Fix Approved",
  FIX_REJECTED = "Fix Rejected",
  FINDINGS_REJECTED = "Findings Rejected",
  NEED_INFORMATION = "Need Information",
  FIX_IN_PROGRESS = "Fix in Progress",
  RISK_ACCEPTED_AND_CLOSED = "Risk Accepted and Closed",
  CLOSE_RETEST = "Close Retest",
  REOPEN_RETEST = "Reopen Retest",
  REQUEST_FURTHER_ACTION = "Request Further Action"
}

// Retest status color mapping
const getRetestStatusColor = (retestStatus: string) => {
  const statusLower = (retestStatus || '').toLowerCase().trim();
  switch (statusLower) {
    case "retest requested":
    case "retest_requested":
      return {
        bg: "bg-purple-50",
        text: "text-purple-700",
        border: "border-purple-200",
        icon: <FiClock className="h-3.5 w-3.5 text-purple-500" />
      };
    case "retest in process":
    case "retest_in_process":
    case "retest in progress":
      return {
        bg: "bg-indigo-50",
        text: "text-indigo-700",
        border: "border-indigo-200",
        icon: <FiClock className="h-3.5 w-3.5 text-indigo-500" />
      };
    case "fix verified":
    case "fix_verified":
    case "fix approved":
    case "close retest":
      return {
        bg: "bg-green-50",
        text: "text-green-700",
        border: "border-green-200",
        icon: <FiCheckCircle className="h-3.5 w-3.5 text-green-500" />
      };
    case "fix not verified":
    case "fix failed":
    case "fix_failed":
    case "fix rejected":
      return {
        bg: "bg-red-50",
        text: "text-red-700",
        border: "border-red-200",
        icon: <FiXCircle className="h-3.5 w-3.5 text-red-500" />
      };
    default:
      return {
        bg: "bg-gray-50",
        text: "text-gray-700",
        border: "border-gray-200",
        icon: <FiInfo className="h-3.5 w-3.5 text-gray-500" />
      };
  }
};

// Status color mapping based on status meaning
const getStatusColor = (
  state: ReportState | undefined,
  status: ReportStatus | undefined,
  retestStatus?: string
) => {
  // Prioritize retest status colors if retest status exists
  if (retestStatus) {
    return getRetestStatusColor(retestStatus);
  }

  // Success/Positive states
  if (
    state === ReportState.BUSINESS_APPROVED ||
    state === ReportState.FIX_VERIFIED ||
    state === ReportState.FIX_APPROVED
  ) {
    return {
      bg: "bg-emerald-50",
      text: "text-emerald-700",
      border: "border-emerald-200",
      icon: <FiCheckCircle className="h-3.5 w-3.5 text-emerald-500" />
    };
  }

  // Warning/Attention states
  if (
    state === ReportState.BUSINESS_REQUEST_INFO ||
    state === ReportState.NEED_INFORMATION ||
    state === ReportState.REQUEST_FURTHER_ACTION ||
    state === ReportState.QA_MODIFIED ||
    state === ReportState.QA_REVIEW_IN_PROCESS
  ) {
    return {
      bg: "bg-amber-50",
      text: "text-amber-700",
      border: "border-amber-200",
      icon: <FiAlertCircle className="h-3.5 w-3.5 text-amber-500" />
    };
  }

  // In Progress/Active states
  if (
    state === ReportState.RETEST_REQUESTED ||
    state === ReportState.RETEST_IN_PROCESS ||
    state === ReportState.FIX_IN_PROGRESS ||
    state === ReportState.REOPEN_RETEST ||
    state === ReportState.BUSINESS_REVIEW ||
    state === ReportState.TRIAGE_APPROVED
  ) {
    return {
      bg: "bg-blue-50",
      text: "text-blue-700",
      border: "border-blue-200",
      icon: <FiClock className="h-3.5 w-3.5 text-blue-500" />
    };
  }

  // Neutral/Completed states
  if (
    state === ReportState.CLOSED ||
    state === ReportState.RISK_ACCEPTED_AND_CLOSED ||
    state === ReportState.CLOSE_RETEST ||
    state === ReportState.BUSINESS_ACCEPT_RISK ||
    state === ReportState.BUSINESS_NON_ACTIONABLE_ISSUE
  ) {
    return {
      bg: "bg-gray-50",
      text: "text-gray-700",
      border: "border-gray-200",
      icon: <FiInfo className="h-3.5 w-3.5 text-gray-500" />
    };
  }

  // Error/Rejected states
  if (
    state === ReportState.BUSINESS_REJECTED ||
    state === ReportState.TRIAGE_REJECTED ||
    state === ReportState.FIX_FAILED ||
    state === ReportState.FIX_REJECTED ||
    state === ReportState.FINDINGS_REJECTED ||
    state === ReportState.DELETED ||
    state === ReportState.OUT_OF_SCOPE
  ) {
    return {
      bg: "bg-red-50",
      text: "text-red-700",
      border: "border-red-200",
      icon: <FiXCircle className="h-3.5 w-3.5 text-red-500" />
    };
  }

  // Draft/Initial states
  if (status === ReportStatus.DRAFT) {
    return {
      bg: "bg-slate-50",
      text: "text-slate-700",
      border: "border-slate-200",
      icon: <FiInfo className="h-3.5 w-3.5 text-slate-500" />
    };
  }

  // Default state
  return {
    bg: "bg-gray-50",
    text: "text-gray-700",
    border: "border-gray-200",
    icon: <FiInfo className="h-3.5 w-3.5 text-gray-500" />
  };
};

const ReportStatusPill = ({
  report,
  className,
  showRetestStatus = false
}: {
  report: CTBReport;
  className?: string;
  showRetestStatus?: boolean;
}) => {
  const { role } = useUserCredentials();

  // Determine which status to show and get appropriate colors
  // Show retest status if requested and report is in Awaiting Fix or Retest Requested states
  const stateStr = (report.state || "").toString().toLowerCase().trim();
  const isAwaitingFixOrRetest =
    report.state === ReportState.AWAITING_FIX ||
    report.state === ReportState.RETEST_REQUESTED ||
    stateStr === "awaiting_fix" ||
    stateStr === "awaiting fix" ||
    stateStr === "retest_requested" ||
    stateStr === "retest requested" ||
    stateStr.includes("retest");

  const shouldShowRetestStatus = showRetestStatus && Boolean(report.retestStatus) && isAwaitingFixOrRetest;
  const statusColor = getStatusColor(
        report.state,
        report.status,
        shouldShowRetestStatus ? report.retestStatus : undefined
      );

  const getStatusText = () => {
    // Always prioritize final states like "closed" over retest status

    if (report.state === ReportState.CLOSED) {
      return ReportPillStatus.CLOSED;
    }

    // If retest status should be shown and exists, show only retest status
    if (shouldShowRetestStatus) {
      return report.retestStatus;
    }
    return getBaseStatusText();
  };

  const getBaseStatusText = () => {
    if (report.status === ReportStatus.DRAFT) {
      return ReportPillStatus.DRAFT;
    }
    if (report.state === ReportState.BUSINESS_APPROVED) {
      return ReportPillStatus.APPROVED;
    }
    if (report.state === ReportState.DELETED) {
      return ReportPillStatus.DELETED;
    }
    if (
      report.state === ReportState.BUSINESS_REJECTED ||
      report.state === ReportState.TRIAGE_REJECTED
    ) {
      return ReportPillStatus.REJECTED;
    }
    if (
      (role === UserRole.ADMIN || role === UserRole.RESEARCHER) &&
      (report.state === ReportState.BUSINESS_REVIEW ||
        report.state === ReportState.TRIAGE_APPROVED)
    ) {
      return "Business Review";
    }
    if (report.state === ReportState.PAYMENT_INITIATED) {
      return ReportPillStatus.PAYMENT_PENDING;
    }
    if (report.state === ReportState.AWAITING_FIX) {
      return ReportPillStatus.AWAITING_FIX;
    }
    if (report.state === ReportState.BUSINESS_REQUEST_INFO) {
      return ReportPillStatus.BUSINESS_REQUEST_INFO;
    }
    if (report.state === ReportState.BUSINESS_NON_ACTIONABLE_ISSUE) {
      return ReportPillStatus.BUSINESS_NON_ACTIONABLE_ISSUE;
    }
    if (report.state === ReportState.OUT_OF_SCOPE) {
      return ReportPillStatus.OUT_OF_SCOPE;
    }
    if (report.state === ReportState.QA_MODIFIED) {
      return ReportPillStatus.QA_MODIFIED;
    }
    if (report.state === ReportState.BUSINESS_ACCEPT_RISK) {
      return ReportPillStatus.BUSINESS_ACCEPT_RISK;
    }
    if (report.state === ReportState.CLOSED) {
      return ReportPillStatus.CLOSED;
    }
    if (report.state === ReportState.RETEST_REQUESTED) {
      return ReportPillStatus.RETEST_REQUESTED;
    }
    if (report.state === ReportState.RETEST_IN_PROCESS) {
      return ReportPillStatus.RETEST_IN_PROCESS;
    }
    if (report.state === ReportState.FIX_VERIFIED) {
      return ReportPillStatus.FIX_VERIFIED;
    }
    if (report.state === ReportState.FIX_FAILED) {
      return ReportPillStatus.FIX_FAILED;
    }
    if (report.state === ReportState.QA_REVIEW_IN_PROCESS) {
      return ReportPillStatus.QA_REVIEW_IN_PROCESS;
    }
    if (report.state === ReportState.FIX_APPROVED) {
      return ReportPillStatus.FIX_APPROVED;
    }
    if (report.state === ReportState.FIX_REJECTED) {
      return ReportPillStatus.FIX_REJECTED;
    }
    if (report.state === ReportState.FINDINGS_REJECTED) {
      return ReportPillStatus.FINDINGS_REJECTED;
    }
    if (report.state === ReportState.NEED_INFORMATION) {
      return ReportPillStatus.NEED_INFORMATION;
    }
    if (report.state === ReportState.FIX_IN_PROGRESS) {
      return ReportPillStatus.FIX_IN_PROGRESS;
    }
    if (report.state === ReportState.RISK_ACCEPTED_AND_CLOSED) {
      return ReportPillStatus.RISK_ACCEPTED_AND_CLOSED;
    }
    if (report.state === ReportState.CLOSE_RETEST) {
      return ReportPillStatus.CLOSE_RETEST;
    }
    if (report.state === ReportState.REOPEN_RETEST) {
      return ReportPillStatus.REOPEN_RETEST;
    }
    if (report.state === ReportState.REQUEST_FURTHER_ACTION) {
      return ReportPillStatus.REQUEST_FURTHER_ACTION;
    }
    return ReportPillStatus.REVIEW;
  };

  return (
    <div
      className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1.5 shadow-sm ${statusColor.bg
        } ${statusColor.text} ${statusColor.border} ${className || ""}`}
    >
      <div className="flex items-center gap-1.5">
        {statusColor.icon}
        <span className="text-xs font-medium tracking-wide">
          {getStatusText()}
        </span>
      </div>
    </div>
  );
};

export default ReportStatusPill;
